import React, { useState, forwardRef } from "react";
import Input, { InputProps } from "components/foundation/Input/Input";
import Select from "components/foundation/Select/Select";
import styled from "styled-components";

interface PhoneInputProps extends InputProps {
  label: string;
  value: string;
  onPhoneChange: (value: string) => void;
  onHasError?: (value: boolean) => void;
}

const PHONE_NUMBER_PREFIX = ["010", "011", "016", "017", "018", "019"];

const formatPhoneNumber = (phoneNumber: string) => {
  if (phoneNumber.length < 5) {
    return phoneNumber;
  }
  const middleNumber = phoneNumber.slice(0, 4);
  const lastNumber = phoneNumber.slice(4, 8);
  return `${middleNumber}-${lastNumber}`;
};

export const UserProfilePhoneInput = forwardRef<
  HTMLInputElement,
  PhoneInputProps
>(({ label, value = "", onPhoneChange, onHasError, ...args }, ref) => {
  // 010 형태로 저장
  const [phonePrefix, setPhonePrefix] = useState(
    value.slice(0, 3) ?? PHONE_NUMBER_PREFIX[0],
  );

  // 00000000 형태로 저장
  const [phoneNumber, setPhoneNumber] = useState(value.slice(3) as string); // 초기값을 빈 문자열로 설정하여 제어 상태 유지

  const handlePhoneNumberChange = (phone: string) => {
    const phoneNumber = phone.replace(/[^0-9]/g, "");
    const prefixNumber = phoneNumber.slice(0, 3);
    const middleNumber = phoneNumber.slice(3, 7);
    const lastNumber = phoneNumber.slice(7);

    if (onPhoneChange) {
      onPhoneChange(`${prefixNumber}${middleNumber}${lastNumber}`);
    }

    setPhonePrefix(prefixNumber);
    setPhoneNumber(middleNumber + lastNumber);

    // 전화번호 자릿수 확인
    const hasError = middleNumber.length + lastNumber.length < 8;
    if (onHasError) {
      onHasError(hasError);
    }
  };

  return (
    <PhoneInputLayout>
      <p>{label}</p>
      <div>
        <PhonePrefixSelect
          list={PHONE_NUMBER_PREFIX.map((prefix) => ({
            value: prefix,
            label: prefix,
          }))}
          value={phonePrefix}
          onValueChange={(value) => {
            handlePhoneNumberChange(`${value}${phoneNumber}`);
          }}
        />
        <Input
          style={{ flex: 1 }}
          type="tel"
          value={formatPhoneNumber(phoneNumber)}
          onChange={(e) =>
            handlePhoneNumberChange(`${phonePrefix}${e.target.value}`)
          }
          ref={ref}
          {...args}
          maxLength={9}
          errorMessage={
            phoneNumber.length < 8
              ? "연락처 형식에 맞춰 입력 필요(11자)"
              : undefined
          }
        />
      </div>
    </PhoneInputLayout>
  );
});

const PhoneInputLayout = styled.div`
  p {
    ${({ theme }) => theme.typography.body1.medium};
    margin-bottom: 1rem;
  }
  > div {
    display: flex;
    gap: 1rem;
  }

  ${({ theme }) => theme.media.small} {
    p {
      ${({ theme }) => theme.typography.body3.medium};
    }
  }
`;

const PhonePrefixSelect = styled(Select)`
  width: 30%;
  height: fit-content;
`;

UserProfilePhoneInput.displayName = "UserProfilePhoneInput";
export default UserProfilePhoneInput;
